import React, { useEffect, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import PropTypes from 'prop-types'

/**
 * TiltNavigation Component
 *
 * Provides subtle orbital camera movement based on mouse movement and device gyroscope.
 * Creates a slight orbiting effect around the scene center for a more comfortable parallax.
 *
 * @param {Object} props
 * @param {number} [props.orbitRadius=2] - Radius of the orbital movement
 * @param {number} [props.mouseSensitivity=0.5] - Mouse orbit sensitivity (radians)
 * @param {number} [props.gyroSensitivity=0.3] - Gyroscope orbit sensitivity (radians)
 * @param {boolean} [props.enabled=true] - Whether tilt navigation is enabled
 */
export function TiltNavigation({
  orbitRadius = 2,
  mouseSensitivity = 0.5,
  gyroSensitivity = 0.3,
  enabled = true
}) {
  const { camera } = useThree()

  // State for tracking input
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [deviceOrientation, setDeviceOrientation] = useState({ beta: 0, gamma: 0 })

  // Store initial camera position for orbital calculations
  const [initialPosition] = useState(() => camera.position.clone())
  const [initialRotation] = useState(() => camera.rotation.clone())

  // Mouse movement handler
  const handleMouseMove = (event) => {
    if (!enabled) return
    const x = (event.clientX / window.innerWidth) * 2 - 1  // Normalize to -1 to 1
    const y = -(event.clientY / window.innerHeight) * 2 + 1 // Normalize to -1 to 1
    setMousePosition({ x, y })
  }

  // Device orientation handler (gyroscope)
  const handleDeviceOrientation = (event) => {
    if (!enabled) return
    // beta: front-to-back tilt (-180 to 180)
    // gamma: left-to-right tilt (-90 to 90)
    setDeviceOrientation({
      beta: event.beta || 0,
      gamma: event.gamma || 0
    })
  }

  // Set up event listeners
  useEffect(() => {
    if (!enabled) return

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('deviceorientation', handleDeviceOrientation)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('deviceorientation', handleDeviceOrientation)
    }
  }, [enabled])

  // Apply orbital camera movement on each frame
  useFrame(() => {
    if (!enabled || !camera) return

    // Mouse-based orbital angle (inverse relationship - right mouse = left orbit)
    const mouseAngleX = -mousePosition.x * mouseSensitivity // Negative for inverse orbit
    const mouseAngleY = mousePosition.y * mouseSensitivity // Equal vertical effect

    // Gyroscope-based orbital angle
    const gyroAngleX = -(deviceOrientation.gamma / 90) * gyroSensitivity // Convert gamma to angle
    const gyroAngleY = (deviceOrientation.beta / 90) * gyroSensitivity // Equal vertical effect

    // Combine mouse and gyroscope inputs
    const angleX = mouseAngleX + gyroAngleX
    const angleY = mouseAngleY + gyroAngleY

    // Calculate orbital position around scene center
    const x = Math.sin(angleX) * orbitRadius
    const y = initialPosition.y + Math.sin(angleY) * orbitRadius // Equal vertical orbit
    const z = Math.cos(angleX) * orbitRadius

    // Apply orbital position
    camera.position.set(
      initialPosition.x + x,
      y,
      initialPosition.z + z
    )

    // Make camera look at the scene center (slight offset for better viewing)
    camera.lookAt(0, 0, 0)
  })

  // This component doesn't render anything visible
  return null
}

TiltNavigation.propTypes = {
  orbitRadius: PropTypes.number,
  mouseSensitivity: PropTypes.number,
  gyroSensitivity: PropTypes.number,
  enabled: PropTypes.bool
}
